import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { 
  ArrowRight, 
  CheckCircle, 
  Clock, 
  FileText, 
  BarChart3,
  Zap,
  UserRound,
  Headphones,
  MessagesSquare,
  ScanSearch,
  Brain,
  Award,
  GraduationCap,
  Check,
  Badge,
  X,
  Building2,
  Calendar
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { fetchKnitAppsFromJson } from '@/services/knitService';
import FrequentlyAskedQuestions from '@/components/shared/FrequentlyAskedQuestions';
import ProvenResults from '@/components/shared/ProvenResults';
import { format } from 'date-fns';
import * as publicBlogApi from '@/api/publicBlogApi';
import { Blog } from '@/types/blog';
import SEO from '@/components/shared/SEO';
import { toast } from 'sonner';
import { Helmet } from 'react-helmet-async';

interface Prospect {
  id: string;
  first_name: string;
  last_name?: string;
  company_name: string;
  email?: string;
  phone?: string;
  job_title?: string;
  industry?: string;
  short_id: string;
  original_url: string;
  created_at: string;
  click_count: number;
  last_clicked_at?: string;
  notes?: string;
}

interface KnitApp {
  id: string;
  label: string;
  logo_short: string;
  logo_full: string;
  category: string;
}

const PersonalizedHomePage = () => {
  const { shortId } = useParams<{ shortId: string }>();
  const [prospect, setProspect] = useState<Prospect | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [atsApps, setAtsApps] = useState<KnitApp[]>([]);
  const [visibleAtsApps, setVisibleAtsApps] = useState<KnitApp[]>([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [scrollY, setScrollY] = useState(0);
  const [blogPosts, setBlogPosts] = useState<Blog[]>([]);
  const [blogsLoading, setBlogsLoading] = useState(true);

  useEffect(() => {
    const fetchProspect = async () => {
      if (!shortId) {
        setError('Invalid URL');
        setLoading(false);
        return;
      }

      try {
        const { data, error: fetchError } = await supabase
          .from('prospects')
          .select('*')
          .eq('short_id', shortId)
          .eq('is_active', true)
          .maybeSingle();

        if (fetchError) {
          throw fetchError;
        }

        if (!data) {
          setError('Personalized page not found');
          setLoading(false);
          return;
        }

        setProspect(data);

        // Update click count and last clicked
        await supabase
          .from('prospects')
          .update({ 
            click_count: (data.click_count || 0) + 1,
            last_clicked_at: new Date().toISOString()
          })
          .eq('id', data.id);

      } catch (err) {
        console.error('Error fetching prospect:', err);
        setError('Failed to load personalized content');
      } finally {
        setLoading(false);
      }
    };

    fetchProspect();
  }, [shortId]);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const loadAtsApps = async () => {
      try {
        const apps = await fetchKnitAppsFromJson();
        setAtsApps(apps);
      } catch (error) {
        console.error('Error loading ATS apps:', error);
      }
    };
    loadAtsApps();
  }, []);

  useEffect(() => {
    if (!atsApps.length) return;
    if (atsApps.length <= 4) {
      setVisibleAtsApps(atsApps);
      return;
    }
    setVisibleAtsApps(atsApps.slice(0, 4));
  }, [atsApps]);

  useEffect(() => {
    const loadBlogPosts = async () => {
      try {
        setBlogsLoading(true);
        const response = await publicBlogApi.getPublicBlogs(1, 3);
        setBlogPosts(response.data || []);
      } catch (error) {
        console.error('Error loading blog posts:', error);
        setBlogPosts([]);
      } finally {
        setBlogsLoading(false);
      }
    };
    loadBlogPosts();
  }, []);

  const handleBookDemo = () => {
    if (prospect?.email) {
      toast.success(`Thanks ${prospect.first_name}! Redirecting you to book a demo...`);
    }
    const targetUrl = prospect?.original_url || '/request-demo';
    window.location.href = targetUrl;
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'MMMM dd, yyyy');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-white to-[#F8F7FF] flex items-center justify-center">
        <div className="text-[#1A1F2C] text-xl">Loading your personalized experience...</div>
      </div>
    );
  }

  if (error || !prospect) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-white to-[#F8F7FF] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4 text-roundone-dark-purple">Page Not Found</h1>
          <p className="mb-4 text-[#1A1F2C]/70">{error || 'The personalized page you\'re looking for doesn\'t exist.'}</p>
          <Button onClick={() => window.location.href = '/'} className="gradient-button">
            Go to Homepage
          </Button>
        </div>
      </div>
    );
  }

  const displayName = prospect.last_name 
    ? `${prospect.first_name} ${prospect.last_name}`
    : prospect.first_name;

  // Create enhanced structured data for the personalized page
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": `RoundOne AI - Personalized Demo for ${displayName} at ${prospect.company_name}`,
    "url": `https://roundone.ai/p/${prospect.short_id}`,
    "description": `Personalized AI-powered candidate screening demo for ${prospect.company_name}. See how RoundOne AI can transform ${prospect.company_name}'s hiring process.`,
    "audience": {
      "@type": "BusinessAudience",
      "audienceType": "HR Professionals, Recruiters, Hiring Managers"
    },
    "about": {
      "@type": "SoftwareApplication",
      "name": "RoundOne AI",
      "applicationCategory": "Human Resources Software",
      "description": "AI-powered candidate assessment platform"
    }
  };

  // Personalized meta tags for social sharing
  const personalizedTitle = `${displayName}, transform ${prospect.company_name}'s hiring with AI`;
  const personalizedDescription = `Personalized demo for ${prospect.company_name}: See how RoundOne AI cuts screening time by 70% with automated phone interviews, custom assessments, and instant candidate insights. Built specifically for ${prospect.company_name}'s hiring needs.`;
  const personalizedUrl = `/p/${prospect.short_id}`;

  const animationStyles = `
    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(10px); }
      to { opacity: 1; transform: translateY(0); }
    }
    .animate-fadeIn {
      animation: fadeIn 0.8s ease-out forwards;
    }
    .dashboard-image-container {
      transition: transform 0.8s cubic-bezier(0.25, 0.1, 0.25, 1); 
    }
    .dashboard-image {
      transition: all 0.8s cubic-bezier(0.25, 0.1, 0.25, 1);
      will-change: transform, box-shadow;
    }
    .dashboard-image img {
      transition: transform 0.8s cubic-bezier(0.25, 0.1, 0.25, 1);
      will-change: transform;
    }
  `;

  return (
    <div className="min-h-screen flex flex-col">
      <SEO 
        title={personalizedTitle}
        description={personalizedDescription}
        url={personalizedUrl}
        image="/images/dashboard-screenshot.png"
        schema={[structuredData]}
      />
      
      {/* Additional meta tags for enhanced social sharing */}
      <Helmet>
        {/* WhatsApp specific */}
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={`RoundOne AI Demo for ${prospect.company_name}`} />
        
        {/* Enhanced Twitter Card */}
        <meta name="twitter:creator" content="@RoundOneAI" />
        <meta name="twitter:site" content="@RoundOneAI" />
        
        {/* LinkedIn specific */}
        <meta property="og:locale" content="en_US" />
        
        {/* Additional WhatsApp preview optimization */}
        <meta name="theme-color" content="#5E4BD2" />
        <meta name="apple-mobile-web-app-title" content={`RoundOne AI - ${prospect.company_name}`} />
        
        {/* Custom meta for tracking shared links */}
        <meta name="prospect-id" content={prospect.short_id} />
        <meta name="prospect-company" content={prospect.company_name} />
      </Helmet>
      
      <style dangerouslySetInnerHTML={{ __html: animationStyles }} />
      
      <Header />

      {/* Hero Section */}
      <section className="pt-16 md:pt-24 pb-72 md:pb-96 bg-white relative">
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center text-center max-w-3xl mx-auto">
            <div className="mb-6">
              <span className="inline-block text-xs bg-roundone-light-purple/10 text-roundone-purple px-3 py-1 rounded-full mb-4">
                <span className="font-bold">PERSONALIZED</span> <span className="ml-1">For {prospect.company_name}</span>
              </span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              <span className="text-roundone-dark-purple">Hello {displayName}!</span><br />
              <span className="text-roundone-light-purple">Shortlist Smarter for {prospect.company_name}</span>
            </h1>
            <p className="text-lg md:text-xl mb-8 text-gray-700">
              Transform {prospect.company_name}'s hiring with AI-driven candidate screening. 
              Say goodbye to CV clutter and identify top talent with data-backed skill evaluations.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 w-full max-w-md mx-auto mb-6">
              <Button 
                onClick={handleBookDemo}
                className="gradient-button py-6 px-8 text-lg w-full rounded-full transition-all duration-300 hover:shadow-lg hover:scale-105 flex items-center justify-center" 
              >
                <Calendar className="mr-2 h-5 w-5" />
                Book Demo for {prospect.company_name}
              </Button>
            </div>
            <div className="flex items-center justify-center mb-24 md:mb-32">
              <div className="flex items-center text-gray-600">
                <div className="flex">
                  {[1, 2, 3, 4, 4.5].map((rating, index) => (
                    <svg 
                      key={index} 
                      className={`w-5 h-5 ${rating === 4.5 ? 'text-yellow-400 fill-current stroke-current' : 'text-yellow-400'}`} 
                      fill={rating === 4.5 ? 'url(#half-gradient)' : 'currentColor'} 
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118l-2.8-2.034c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
                    </svg>
                  ))}
                </div>
                <span className="ml-2">100+ Smart Recruiters like {prospect.company_name} using RoundOne AI</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Dashboard image */}
        <div className="absolute left-0 right-0 bottom-0 translate-y-1/3 flex justify-center px-4">
          <div 
            className="w-full max-w-6xl dashboard-image-container"
            style={{
              transform: `scale(${Math.min(1 + scrollY * 0.0003, 1.08)})`,
            }}
          >
            <div 
              className="bg-roundone-dark-purple rounded-xl p-4 shadow-2xl overflow-hidden border border-[#E5DEFF] dashboard-image"
              style={{
                transform: `translateY(${Math.min(scrollY * 0.07, 25)}px)`,
                boxShadow: `0 ${Math.min(10 + scrollY * 0.03, 20)}px ${Math.min(15 + scrollY * 0.05, 35)}px rgba(94, 75, 210, ${Math.min(0.2 + scrollY * 0.0005, 0.35)})`,
              }}
            >
              <img 
                src="/images/dashboard-screenshot.png" 
                alt="RoundOne AI Dashboard" 
                className="w-full h-auto rounded-lg"
                style={{
                  transform: `scale(${Math.min(1 + scrollY * 0.0001, 1.03)})`,
                }}
                onError={(e) => {
                  e.currentTarget.src = 'https://placehold.co/1200x600/1A0B36/9b87f5?text=RoundOne+Dashboard';
                }}
              />
            </div>
          </div>
        </div>
      </section>

      <div className="w-full bg-gradient-to-r from-transparent via-[#9b87f5]/30 to-transparent h-px"></div>

      {/* How It Works Section */}
      <section className="pt-72 md:pt-96 pb-16 md:pb-20 bg-[#F8F7FF]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 mt-24">
            <p className="uppercase text-sm font-medium tracking-wider text-[#9b87f5] mb-2">HOW IT WORKS</p>
            <h2 className="text-3xl font-bold mb-4">
              Transform {prospect.company_name}'s Hiring in 4 Steps
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Stop drowning in resumes. RoundOne AI helps {prospect.company_name} identify top talent faster
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 max-w-7xl mx-auto">
            <div className="relative h-full">
              <div className="bg-white p-6 rounded-xl border border-[#9b87f5]/20 shadow-sm h-full flex flex-col">
                <div className="w-12 h-12 bg-[#9b87f5] rounded-full flex items-center justify-center text-white font-bold mb-4 flex-shrink-0">
                  1
                </div>
                <h3 className="text-lg font-semibold text-[#1A1F2C] mb-2">Create Assessments for {prospect.company_name}</h3>
                <p className="text-[#1A1F2C]/70 flex-grow">
                  Select skills and build custom assessments tailored to {prospect.company_name}'s roles in minutes, not hours.
                </p>
              </div>
              <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                <ArrowRight className="text-[#9b87f5]" size={24} />
              </div>
            </div>
            
            <div className="relative h-full">
              <div className="bg-white p-6 rounded-xl border border-[#9b87f5]/20 shadow-sm h-full flex flex-col">
                <div className="w-12 h-12 bg-[#9b87f5] rounded-full flex items-center justify-center text-white font-bold mb-4 flex-shrink-0">
                  2
                </div>
                <h3 className="text-lg font-semibold text-[#1A1F2C] mb-2">Screen {prospect.company_name}'s Candidates</h3>
                <p className="text-[#1A1F2C]/70 flex-grow">
                  Send invites or let candidates complete phone screenings with our AI interviewer - cut {prospect.company_name}'s admin time by 70%.
                </p>
              </div>
              <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                <ArrowRight className="text-[#9b87f5]" size={24} />
              </div>
            </div>
            
            <div className="relative h-full">
              <div className="bg-white p-6 rounded-xl border border-[#9b87f5]/20 shadow-sm h-full flex flex-col">
                <div className="w-12 h-12 bg-[#9b87f5] rounded-full flex items-center justify-center text-white font-bold mb-4 flex-shrink-0">
                  3
                </div>
                <h3 className="text-lg font-semibold text-[#1A1F2C] mb-2">Get Insights</h3>
                <p className="text-[#1A1F2C]/70 flex-grow">
                  Receive detailed candidate evaluations with skill scores and AI-generated summaries for {prospect.company_name}.
                </p>
              </div>
              <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                <ArrowRight className="text-[#9b87f5]" size={24} />
              </div>
            </div>
            
            <div className="h-full">
              <div className="bg-white p-6 rounded-xl border border-[#9b87f5]/20 shadow-sm h-full flex flex-col">
                <div className="w-12 h-12 bg-[#9b87f5] rounded-full flex items-center justify-center text-white font-bold mb-4 flex-shrink-0">
                  4
                </div>
                <h3 className="text-lg font-semibold text-[#1A1F2C] mb-2">Hire Faster</h3>
                <p className="text-[#1A1F2C]/70 flex-grow">
                  Help {prospect.company_name} make data-driven decisions and reduce time-to-hire by up to 60%.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="h-px max-w-6xl mx-auto bg-gradient-to-r from-transparent via-[#9b87f5]/30 to-transparent"></div>

      {/* ATS Integrations Section - same as original but with company name */}
      <section className="py-16 md:py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#E9E5FF] to-[#F0EEFF]"></div>
        <div className="container relative mx-auto px-4 max-w-7xl">
          <div className="text-center mb-12">
            <p className="uppercase text-sm font-medium tracking-wider text-[#9b87f5] mb-2">SEAMLESS INTEGRATIONS</p>
            <h2 className="text-3xl font-bold mb-4">Connect With {prospect.company_name}'s ATS</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              RoundOne integrates with {prospect.company_name}'s existing Applicant Tracking System for a frictionless workflow
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-center max-w-6xl mx-auto">
            <div>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="bg-[#F8F7FF] p-2 rounded-full">
                    <CheckCircle className="h-5 w-5 text-[#9b87f5]" />
                  </div>
                  <div>
                    <h3 className="font-medium">Automated Data Sync for {prospect.company_name}</h3>
                    <p className="text-muted-foreground text-sm">Candidate data flows automatically between platforms</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-[#F8F7FF] p-2 rounded-full">
                    <Zap className="h-5 w-5 text-[#9b87f5]" />
                  </div>
                  <div>
                    <h3 className="font-medium">Unified Workflow</h3>
                    <p className="text-muted-foreground text-sm">Manage {prospect.company_name}'s hiring from a single platform</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-[#F8F7FF] p-2 rounded-full">
                    <Clock className="h-5 w-5 text-[#9b87f5]" />
                  </div>
                  <div>
                    <h3 className="font-medium">Real-time Updates</h3>
                    <p className="text-muted-foreground text-sm">Screening results sync instantly to {prospect.company_name}'s ATS</p>
                  </div>
                </div>
              </div>
              
              <div className="mt-8">
                <Button 
                  onClick={handleBookDemo}
                  className="bg-roundone-dark-purple text-white py-6 px-8 text-lg rounded-full transition-all duration-300 hover:bg-roundone-purple hover:shadow-md flex items-center justify-center"
                >
                  See Demo for {prospect.company_name}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-sm border border-[#E5DEFF]">
                <div className="grid grid-cols-2 gap-4 mb-4">
                  {visibleAtsApps.length > 0 ? (
                    visibleAtsApps.map((app, index) => (
                      <div 
                        key={`${app.id}-${index}`}
                        className="bg-white border border-[#E5DEFF] rounded-lg p-4 flex flex-col items-center justify-center"
                      >
                        <img 
                          src={app.logo_short || `/ats-logos/${app.id}.png`} 
                          alt={app.label} 
                          className="w-12 h-12 object-contain mb-2" 
                          onError={(e) => {
                            e.currentTarget.src = `https://placehold.co/100x100/F8F7FF/9b87f5?text=${encodeURIComponent(app.label)}`;
                          }}
                        />
                        <h3 className="text-center text-sm font-medium">{app.label}</h3>
                      </div>
                    ))
                  ) : (
                    <>
                      <div className="bg-white border border-[#E5DEFF] rounded-lg p-4 flex flex-col items-center justify-center">
                        <div className="w-12 h-12 bg-[#F8F7FF] rounded flex items-center justify-center mb-2">
                          <span className="text-[#9b87f5] font-bold text-xs">GH</span>
                        </div>
                        <h3 className="text-center text-sm font-medium">Greenhouse</h3>
                      </div>
                      <div className="bg-white border border-[#E5DEFF] rounded-lg p-4 flex flex-col items-center justify-center">
                        <div className="w-12 h-12 bg-[#F8F7FF] rounded flex items-center justify-center mb-2">
                          <span className="text-[#9b87f5] font-bold text-xs">AS</span>
                        </div>
                        <h3 className="text-center text-sm font-medium">Ashby</h3>
                      </div>
                      <div className="bg-white border border-[#E5DEFF] rounded-lg p-4 flex flex-col items-center justify-center">
                        <div className="w-12 h-12 bg-[#F8F7FF] rounded flex items-center justify-center mb-2">
                          <span className="text-[#9b87f5] font-bold text-xs">TT</span>
                        </div>
                        <h3 className="text-center text-sm font-medium">Team Tailor</h3>
                      </div>
                      <div className="bg-white border border-[#E5DEFF] rounded-lg p-4 flex flex-col items-center justify-center">
                        <div className="w-12 h-12 bg-[#F8F7FF] rounded flex items-center justify-center mb-2">
                          <span className="text-[#9b87f5] font-bold text-xs">BH</span>
                        </div>
                        <h3 className="text-center text-sm font-medium">Bullhorn</h3>
                      </div>
                    </>
                  )}
                </div>
              </div>
              <div className="absolute -bottom-4 -left-4 bg-[#F8F7FF] border border-[#9b87f5]/30 p-4 rounded-lg shadow-md hidden md:block">
                <p className="font-medium text-[#5E4BD2]">Two-click integration</p>
                <p className="text-sm text-muted-foreground">Perfect for {prospect.company_name}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Proven Results Section - personalized */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-[#5E4BD2] to-[#7662DA]">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto rounded-2xl overflow-hidden">
            <div className="p-8 md:p-12 text-center">
              <div className="inline-block bg-white/20 text-white text-xs font-medium px-3 py-1 rounded-full backdrop-blur-sm mb-6">
                PROVEN RESULTS FOR COMPANIES LIKE {prospect.company_name.toUpperCase()}
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
                Help {prospect.company_name} Slash Screening Time by 70%
              </h2>
              <p className="text-white/90 text-lg max-w-2xl mx-auto mb-8">
                Join 1000+ forward-thinking companies like {prospect.company_name} who are saving time, reducing costs and making better hires with RoundOne AI
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-10">
                <div className="bg-white/10 p-6 rounded-xl backdrop-blur-sm">
                  <div className="text-3xl font-bold text-white mb-2">70%</div>
                  <div className="text-white/80">Faster Screening for {prospect.company_name}</div>
                </div>
                <div className="bg-white/10 p-6 rounded-xl backdrop-blur-sm">
                  <div className="text-3xl font-bold text-white mb-2">60%</div>
                  <div className="text-white/80">Reduced Time-to-Hire</div>
                </div>
                <div className="bg-white/10 p-6 rounded-xl backdrop-blur-sm">
                  <div className="text-3xl font-bold text-white mb-2">80%</div>
                  <div className="text-white/80">Better Quality Hires</div>
                </div>
                <div className="bg-white/10 p-6 rounded-xl backdrop-blur-sm">
                  <div className="text-3xl font-bold text-white mb-2">95%</div>
                  <div className="text-white/80">Candidate Satisfaction</div>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row justify-center gap-4 max-w-xl mx-auto">
                <Button 
                  onClick={handleBookDemo}
                  className="bg-white text-roundone-dark-purple py-6 px-8 text-lg rounded-full transition-all duration-300 hover:bg-white/90 hover:shadow-lg hover:scale-105 w-full sm:w-auto flex items-center justify-center" 
                >
                  <Calendar className="mr-2 h-5 w-5" />
                  Book Demo for {prospect.company_name}
                </Button>
                <Button 
                  onClick={handleBookDemo}
                  className="gradient-button py-6 px-8 text-lg rounded-full transition-all duration-300 hover:shadow-lg hover:scale-105 w-full sm:w-auto flex items-center justify-center" 
                >
                  Get started for free
                  <Zap className="ml-2 h-5 w-5 animate-pulse" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section - personalized */}
      <section className="py-16 md:py-20 bg-[#F8F7FF]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <p className="uppercase text-sm font-medium tracking-wider text-[#9b87f5] mb-2">PERFECT FOR {prospect.company_name.toUpperCase()}</p>
            <h2 className="text-3xl font-bold mb-4">Beyond Basic Screening Tools</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Our AI-powered platform delivers lightning-fast screening with unmatched accuracy for companies like {prospect.company_name}
            </p>
          </div>
          
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-gradient-to-br from-white to-white/90 rounded-xl p-8 shadow-sm border border-[#E5DEFF] transition-all duration-300 hover:shadow-lg hover:border-[#9b87f5] hover:translate-y-[-5px] group relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-tr from-[#9b87f5]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="bg-[#F8F7FF] p-4 rounded-xl w-16 h-16 mb-6 flex items-center justify-center transition-colors duration-300 group-hover:bg-[#9b87f5] relative z-10">
                  <Headphones className="h-7 w-7 text-[#9b87f5] transition-all duration-300 group-hover:text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-4 relative z-10">Voice & Phone Screening</h3>
                <p className="text-muted-foreground relative z-10">
                  Help {prospect.company_name} save hours with phone screenings by our natural-sounding AI interviewer.
                </p>
              </div>
              
              <div className="bg-gradient-to-br from-white to-white/90 rounded-xl p-8 shadow-sm border border-[#E5DEFF] transition-all duration-300 hover:shadow-lg hover:border-[#9b87f5] hover:translate-y-[-5px] group relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-tr from-[#9b87f5]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="bg-[#F8F7FF] p-4 rounded-xl w-16 h-16 mb-6 flex items-center justify-center transition-colors duration-300 group-hover:bg-[#9b87f5] relative z-10">
                  <MessagesSquare className="h-7 w-7 text-[#9b87f5] transition-all duration-300 group-hover:text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-4 relative z-10">Custom Questions for {prospect.company_name}</h3>
                <p className="text-muted-foreground relative z-10">
                  Create role-specific screening questions that evaluate exactly the skills {prospect.company_name} needs.
                </p>
              </div>
              
              <div className="bg-gradient-to-br from-white to-white/90 rounded-xl p-8 shadow-sm border border-[#E5DEFF] transition-all duration-300 hover:shadow-lg hover:border-[#9b87f5] hover:translate-y-[-5px] group relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-tr from-[#9b87f5]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="bg-[#F8F7FF] p-4 rounded-xl w-16 h-16 mb-6 flex items-center justify-center transition-colors duration-300 group-hover:bg-[#9b87f5] relative z-10">
                  <ScanSearch className="h-7 w-7 text-[#9b87f5] transition-all duration-300 group-hover:text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-4 relative z-10">Time-Saving Analytics</h3>
                <p className="text-muted-foreground relative z-10">
                  Give {prospect.company_name} instant candidate rankings and AI-generated summaries that cut decision time by 80%.
                </p>
              </div>
            </div>
            
            <div className="mt-12 text-center">
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Button 
                  onClick={handleBookDemo}
                  className="bg-white text-roundone-dark-purple py-3 px-6 rounded-full transition-all duration-300 hover:bg-white/90 hover:shadow-lg hover:scale-105 flex items-center justify-center w-full sm:w-auto"
                >
                  <Calendar className="mr-2 h-4 w-4" />
                  Book Demo for {prospect.company_name}
                </Button>
                <Button 
                  onClick={handleBookDemo}
                  className="gradient-button py-3 px-6 rounded-full transition-all duration-300 hover:shadow-lg hover:scale-105 flex items-center justify-center w-full sm:w-auto"
                >
                  Get started for free
                  <Zap className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-lg p-10 border border-[#E5DEFF]">
            <div className="text-center">
              <span className="inline-block text-xs bg-roundone-light-purple text-white px-3 py-1 rounded-full mb-4">
                READY FOR {prospect.company_name.toUpperCase()}
              </span>
              <h2 className="text-3xl font-bold mb-4">Ready for a Chat, {prospect.first_name}?</h2>
              <p className="text-lg text-muted-foreground mb-8 max-w-xl mx-auto">
                Let's discuss how RoundOne AI can revolutionize {prospect.company_name}'s hiring process. 
                Book a personalized demo today.
              </p>
              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <Button 
                  onClick={handleBookDemo}
                  className="gradient-button py-6 px-8 text-lg rounded-full transition-all duration-300 hover:shadow-lg hover:translate-y-[-2px]"
                >
                  <Calendar className="mr-2" />
                  Book Demo for {prospect.company_name}
                </Button>
              </div>
              
              {/* Contact Info */}
              {(prospect.job_title || prospect.email) && (
                <div className="mt-8 text-center text-[#1A1F2C]/70">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Building2 className="w-4 h-4" />
                    <span>
                      {prospect.job_title && `${prospect.job_title} at `}
                      {prospect.company_name}
                    </span>
                  </div>
                  {prospect.email && (
                    <p className="text-sm opacity-75">{prospect.email}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PersonalizedHomePage; 